import {
  Home,
  ListBulleted,
  Information,
  Chat,
  Categories,
  Tag,
  Search,
  Scales,
  Building,
  User,
  Delivery,
  ChartLineData,
  Receipt,
} from "@carbon/icons-react";
import { WHATSAPP } from "./enums";
import { ABILITIES } from "./abilities";
import type { NavItem } from "../components/carbon/AppShell/AppShellNav";

// Nav labels are i18n KEYS, not pre-translated strings. They used to be `i18next.t(...)` evaluated
// once at module load, which froze the sidebar in whatever language was active at first import — so
// changing language never updated the nav until a full reload. AppShellNav now translates each key
// at render via `useTranslation`, so the sidebar re-translates live with the rest of the app.

// ONE buyer catalog, filtered at render by `can(item.ability)` in Layout. Items with no `ability`
// (Home) are always shown. Each ability matches the one guarding the same section's route in the
// unified buyer route tree, so the nav never links to a page the route guard would reject.
//
// `module: "purchaseOrders"` additionally requires the company's OC module flag
// (`purchaseOrderModuleEnabled`) — see `isBuyerNavItemVisible`.
export type BuyerNavItem = NavItem & {
  ability?: string;
  module?: "purchaseOrders";
};

export const buyerNavigation: BuyerNavItem[] = [
  { name: "navigation.home", href: "/", icon: Home },
  {
    name: "navigation.materials",
    href: "/materials",
    icon: ListBulleted,
    ability: ABILITIES.VIEW_MATERIALS,
  },
  {
    name: "navigation.purchaseRequests",
    href: "/purchase-requests",
    icon: Categories,
    ability: ABILITIES.VIEW_PURCHASE_REQUESTS,
  },
  {
    name: "navigation.tenders",
    href: "/orders",
    icon: Scales,
    ability: ABILITIES.VIEW_ORDERS,
  },
  {
    name: "purchaseOrders.nav",
    href: "/purchase-orders",
    icon: Receipt,
    ability: ABILITIES.VIEW_PURCHASE_ORDERS,
    module: "purchaseOrders",
  },
  {
    name: "navigation.delivery",
    href: "/deliveries",
    icon: Delivery,
    ability: ABILITIES.VIEW_DELIVERIES,
  },
  {
    name: "navigation.suppliers",
    href: "/suppliers",
    icon: Building,
    ability: ABILITIES.VIEW_SUPPLIERS,
  },
  {
    name: "navigation.users",
    href: "/users",
    icon: User,
    ability: ABILITIES.VIEW_USERS,
  },
  {
    name: "navigation.tags",
    href: "/tags",
    icon: Tag,
    ability: ABILITIES.VIEW_TAGS,
  },
  {
    name: "navigation.chat",
    href: "/chat",
    icon: Chat,
    ability: ABILITIES.VIEW_CHAT,
  },
  {
    name: "analytics.title",
    href: "/analytics",
    icon: ChartLineData,
    ability: ABILITIES.VIEW_ANALYTICS,
  },
];

// Single visibility predicate for the buyer nav (used by Layout): the item's ability, plus its
// module flag when it has one. Mirrors the RequireAbility + RequirePurchaseOrderModule route guards.
export const isBuyerNavItemVisible = (
  item: BuyerNavItem,
  ctx: {
    can: (ability: string) => boolean;
    purchaseOrderModuleEnabled: boolean;
  }
): boolean =>
  (!item.ability || ctx.can(item.ability)) &&
  (item.module !== "purchaseOrders" || ctx.purchaseOrderModuleEnabled);

// ONE seller catalog, filtered at render by `can(item.ability)` in Layout (mirrors buyerNavigation).
// Home has no `ability` so it is always shown. Each ability matches the RequireAbility guard on the
// same section's route in UnifiedSellerRoutes, so the nav never links to a page the guard rejects.
export const sellerNavigation: Array<NavItem & { ability?: string }> = [
  {
    name: "navigation.home",
    href: "/",
    icon: Home,
    current: true,
  },
  {
    name: "navigation.companyProfile",
    href: "/company",
    icon: Building,
    current: false,
    ability: ABILITIES.SELLER_VIEW_COMPANY,
  },
  {
    name: "navigation.opportunities",
    href: "/dashboard",
    icon: Search,
    current: false,
    ability: ABILITIES.SELLER_VIEW_OPPORTUNITIES,
  },
  {
    name: "navigation.materialOffers",
    href: "/material/offers",
    icon: ListBulleted,
    current: false,
    ability: ABILITIES.SELLER_SUBMIT_OFFERS,
  },
  {
    name: "navigation.tenders",
    href: "/biddings",
    icon: Scales,
    current: false,
    ability: ABILITIES.SELLER_VIEW_BIDDINGS,
  },
  {
    name: "navigation.delivery",
    href: "/deliveries",
    icon: Delivery,
    current: false,
    ability: ABILITIES.SELLER_VIEW_DELIVERIES,
  },
  {
    name: "purchaseOrders.supplier.nav",
    href: "/purchase-orders",
    icon: Receipt,
    current: false,
    ability: ABILITIES.SELLER_RESPOND_PURCHASE_ORDERS,
  },
  {
    name: "navigation.users",
    href: "/users",
    icon: User,
    current: false,
    ability: ABILITIES.SELLER_VIEW_USERS,
  },
  {
    name: "navigation.chat",
    href: "/chat",
    icon: Chat,
    current: false,
    ability: ABILITIES.SELLER_VIEW_CHAT,
  },
];

export const pendingApprovalNavigation = [
  {
    name: "navigation.home",
    href: "/",
    icon: Home,
    current: true,
  },
];

export const secondaryNavigation = [
  {
    href: WHATSAPP,
    name: "navigation.support",
    icon: Information,
    current: true,
  },
];
